/**
 * Security · IOC Blocklist — list of indicators (hashes / IPs / domains
 * / cert SHA1s) the sensors should match against and block.
 *
 * Wire contract: see `LOVABLE_TODO.md` (2026-06-06).
 *   GET    /admin/iocs?kind=&severity=
 *   POST   /admin/iocs
 *   POST   /admin/iocs/bulk
 *   DELETE /admin/iocs/:id
 *
 * Source families auto-imported: `threatintel:` (1h cadence),
 * `manual:` (operator), `playbook:` (auto-remediation).
 */
import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Database, Plus, ShieldX, Trash2, Upload } from "lucide-react";

import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { IocAddDialog } from "@/components/security/iocs/IocAddDialog";
import { IocBulkImportDialog } from "@/components/security/iocs/IocBulkImportDialog";
import { useDeleteIoc, useIocs } from "@/hooks/security/useIocs";
import {
  iocSourceFamily,
  type IocKind,
  type IocRecord,
  type IocSeverity,
  type IocSourceFamily,
} from "@/types/edr-ioc";

const KINDS: Array<IocKind | "all"> = [
  "all",
  "sha256",
  "md5",
  "ip",
  "domain",
  "cert_sha1",
];
const SEVS: Array<IocSeverity | "all"> = [
  "all",
  "critical",
  "high",
  "medium",
  "low",
  "info",
];

type SourceFilter = "all" | IocSourceFamily;

const SEV_TONE: Record<IocSeverity, string> = {
  critical: "bg-destructive/10 text-destructive border-destructive/30",
  high: "bg-destructive/10 text-destructive border-destructive/30",
  medium: "bg-warning-muted text-warning border-warning/30",
  low: "bg-info/10 text-info border-info/30",
  info: "bg-muted text-muted-foreground border-border",
};

const SOURCE_LABEL: Record<IocSourceFamily, string> = {
  threatintel: "TI feeds",
  manual: "Operator",
  playbook: "Playbook",
  other: "Other",
};

const SOURCE_TONE: Record<IocSourceFamily, string> = {
  threatintel: "bg-info/10 text-info border-info/30",
  manual: "bg-primary/10 text-primary border-primary/30",
  playbook: "bg-warning-muted text-warning border-warning/30",
  other: "bg-muted text-muted-foreground border-border",
};

export default function SecurityIocsPage() {
  return (
    <ConnectorGate connectorId="edr">
      <IocsInner />
    </ConnectorGate>
  );
}

function IocsInner() {
  const [kind, setKind] = useState<IocKind | "all">("all");
  const [severity, setSeverity] = useState<IocSeverity | "all">("all");
  const [source, setSource] = useState<SourceFilter>("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<IocRecord | null>(null);

  const iocsQ = useIocs({
    kind: kind === "all" ? undefined : kind,
    severity: severity === "all" ? undefined : severity,
    limit: 500,
  });
  const remove = useDeleteIoc();
  const all = iocsQ.data ?? [];

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all.filter((ioc) => {
      if (source !== "all" && iocSourceFamily(ioc.source) !== source) return false;
      if (!ql) return true;
      return `${ioc.value} ${ioc.source ?? ""} ${ioc.note ?? ""}`
        .toLowerCase()
        .includes(ql);
    });
  }, [all, source, q]);

  const counts = useMemo(() => {
    const total = all.length;
    let ti = 0, manual = 0, playbook = 0;
    for (const i of all) {
      const f = iocSourceFamily(i.source);
      if (f === "threatintel") ti++;
      else if (f === "manual") manual++;
      else if (f === "playbook") playbook++;
    }
    return { total, ti, manual, playbook };
  }, [all]);

  return (
    <SurfaceShell
      overline="Security · IOC Blocklist"
      title="Indicators sensors will block on sight"
      summary="Hashes, IPs, domains, and cert fingerprints. Threat-intel feeds auto-sync hourly; operators and playbooks can pin their own."
      kpis={
        <>
          <StatTile label="Total" value={counts.total} loading={iocsQ.isLoading || iocsQ.isError} />
          <StatTile label="From TI" value={counts.ti} loading={iocsQ.isLoading || iocsQ.isError} hint="Auto-imported hourly" />
          <StatTile label="Operator" value={counts.manual} loading={iocsQ.isLoading || iocsQ.isError} hint="Pinned by analysts" />
          <StatTile label="Playbook" value={counts.playbook} loading={iocsQ.isLoading || iocsQ.isError} hint="Auto-remediation" />
        </>
      }
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk import
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add IOC
          </Button>
        </>
      }
    >
      <Panel
        title={`Indicators · ${filtered.length} of ${all.length}`}
        loading={iocsQ.isLoading}
        isError={iocsQ.isError}
        error={iocsQ.error}
        onRetry={() => iocsQ.refetch()}
        empty={!iocsQ.isLoading && !iocsQ.isError && filtered.length === 0}
        emptyTitle={all.length === 0 ? "No IOCs yet" : "No matches"}
        emptyDescription={
          all.length === 0
            ? "Threat-intel feeds will start populating within an hour. Pin your own with Add IOC or Bulk import."
            : "Try clearing the filters."
        }
        skeleton="table"
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search value, source, note…"
            className="h-8 w-[240px] text-xs"
          />
          <Select value={kind} onValueChange={(v) => setKind(v as IocKind | "all")}>
            <SelectTrigger className="h-8 w-[120px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((k) => (
                <SelectItem key={k} value={k} className="text-xs">
                  {k === "all" ? "All kinds" : k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={severity} onValueChange={(v) => setSeverity(v as IocSeverity | "all")}>
            <SelectTrigger className="h-8 w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEVS.map((s) => (
                <SelectItem key={s} value={s} className="text-xs">
                  {s === "all" ? "All severities" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Source</span>
            <ToggleGroup
              type="single"
              value={source}
              onValueChange={(v) => setSource((v as SourceFilter) || "all")}
              size="sm"
            >
              <ToggleGroupItem value="all" className="h-7 px-2 text-[11px]">All</ToggleGroupItem>
              <ToggleGroupItem value="threatintel" className="h-7 px-2 text-[11px]">TI</ToggleGroupItem>
              <ToggleGroupItem value="manual" className="h-7 px-2 text-[11px]">Operator</ToggleGroupItem>
              <ToggleGroupItem value="playbook" className="h-7 px-2 text-[11px]">Playbook</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div className="-mx-4 -my-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[90px] text-[10px] uppercase tracking-wider">Kind</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Value</TableHead>
                <TableHead className="w-[100px] text-[10px] uppercase tracking-wider">Severity</TableHead>
                <TableHead className="w-[160px] text-[10px] uppercase tracking-wider">Source</TableHead>
                <TableHead className="w-[120px] text-[10px] uppercase tracking-wider">Added</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((ioc) => {
                const fam = iocSourceFamily(ioc.source);
                return (
                  <TableRow key={ioc.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {ioc.kind}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-foreground truncate max-w-[360px]" title={ioc.value}>
                      {ioc.value}
                      {ioc.note && (
                        <span className="block text-[10px] text-muted-foreground truncate">
                          {ioc.note}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                          SEV_TONE[ioc.severity],
                        )}
                      >
                        {ioc.severity}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            "inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium",
                            SOURCE_TONE[fam],
                          )}
                        >
                          {SOURCE_LABEL[fam]}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[100px]" title={ioc.source}>
                          {ioc.source?.split(":").slice(1).join(":") || ioc.source || ""}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {formatDistanceToNow(new Date(ioc.createdAt), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => setConfirmDelete(ioc)}
                        title="Remove from blocklist"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <IocAddDialog open={addOpen} onOpenChange={setAddOpen} />
      <IocBulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} />

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove IOC?</AlertDialogTitle>
            <AlertDialogDescription>
              Sensors will stop matching on this indicator within ~30s. This cannot be undone — re-add it via the bulk importer if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmDelete && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs font-mono break-all">
              <span className="text-muted-foreground">{confirmDelete.kind}</span> {confirmDelete.value}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (confirmDelete) {
                  remove.mutate(confirmDelete.id, {
                    onSuccess: () => setConfirmDelete(null),
                  });
                }
              }}
            >
              {remove.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SurfaceShell>
  );
}
